<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { ref, watch } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  generateFunnelProposal,
  applyFunnelProposal,
} from 'dashboard/api/aceleres/funnelAssistant';

const props = defineProps({
  show: Boolean,
  accountId: { type: [String, Number], default: '' },
});
const emit = defineEmits(['update:show', 'applied']);
const state = ref('input');
const description = ref('');
const proposal = ref({ funnels: [] });
const error = ref('');
const busy = ref(false);

watch(
  () => props.show,
  visible => {
    if (visible) {
      state.value = 'input';
      proposal.value = { funnels: [] };
      error.value = '';
    }
  }
);

const close = () => {
  if (!busy.value) emit('update:show', false);
};

const generate = async () => {
  if (busy.value || description.value.trim().length < 20) return;
  busy.value = true;
  error.value = '';
  state.value = 'loading';
  try {
    const response = await generateFunnelProposal(
      props.accountId,
      description.value
    );
    const nextProposal = response?.proposal;
    if (!Array.isArray(nextProposal?.funnels) || !nextProposal.funnels.length) {
      throw new Error('A proposta retornada não contém funis válidos.');
    }
    proposal.value = JSON.parse(JSON.stringify(nextProposal));
    state.value = 'review';
  } catch (requestError) {
    error.value = requestError?.message || 'Não foi possível gerar a proposta.';
    state.value = 'input';
  } finally {
    busy.value = false;
  }
};

const removeFunnel = index => proposal.value.funnels.splice(index, 1);
const removeStage = (funnel, index) => funnel.stages.splice(index, 1);

const apply = async () => {
  if (busy.value || !proposal.value.funnels.length) return;
  busy.value = true;
  error.value = '';
  try {
    await applyFunnelProposal(props.accountId, proposal.value);
    emit('applied');
    emit('update:show', false);
  } catch (requestError) {
    error.value =
      requestError?.message || 'Não foi possível criar a estrutura.';
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <Modal :show="show" :on-close="close" @close="close">
    <form
      class="acl-funnel-assistant"
      @submit.prevent="state === 'input' ? generate() : apply()"
    >
      <header class="acl-funnel-assistant__header">
        <div class="acl-funnel-assistant__heading">
          <span class="acl-funnel-assistant__badge" aria-hidden="true">IA</span>
          <div>
            <h2 class="text-lg font-medium text-n-slate-12">
              Desenhar funil com IA
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              Descreva seu processo comercial e revise a sugestão.
            </p>
          </div>
        </div>
      </header>

      <div v-if="state === 'input'" class="acl-funnel-assistant__content">
        <label class="acl-funnel-assistant__field text-sm text-n-slate-12">
          <span class="font-medium">Como sua empresa vende?</span>
          <span class="text-xs text-n-slate-10">
            Conte os principais passos, públicos e critérios de avanço.
          </span>
          <textarea
            v-model="description"
            rows="8"
            minlength="20"
            maxlength="12000"
            placeholder="Descreva o processo comercial, públicos, tipos de venda e principais passos..."
          />
        </label>
        <p class="text-xs text-n-slate-10">
          Use pelo menos 20 caracteres para gerar uma proposta mais útil.
        </p>
        <NextButton
          type="submit"
          label="Gerar Proposta"
          :is-loading="busy"
          :disabled="description.trim().length < 20"
        />
      </div>

      <div
        v-else-if="state === 'loading'"
        class="acl-funnel-assistant__loading"
      >
        <span class="acl-funnel-assistant__loader" aria-hidden="true" />
        <strong class="text-sm text-n-slate-12">
          A IA está analisando seu processo comercial
        </strong>
        <span class="text-xs text-n-slate-11">
          Isso pode levar alguns segundos. Nada será salvo nesta etapa.
        </span>
      </div>

      <div v-else class="acl-funnel-assistant__review">
        <div class="acl-funnel-assistant__notice">
          <span class="acl-funnel-assistant__notice-icon" aria-hidden="true">
            ✓
          </span>
          <div>
            <strong class="text-sm text-n-slate-12">Revise a proposta</strong>
            <p class="mt-1 text-xs text-n-slate-11">
              Nada será criado até você clicar em “Aprovar e Criar”.
            </p>
          </div>
        </div>
        <section
          v-for="(funnel, funnelIndex) in proposal.funnels"
          :key="funnelIndex"
          class="acl-funnel-assistant__funnel"
        >
          <div class="acl-funnel-assistant__funnel-header">
            <div class="acl-funnel-assistant__funnel-title">
              <span class="acl-funnel-assistant__step-number">{{
                funnelIndex + 1
              }}</span>
              <input
                v-model="funnel.name"
                class="min-w-0 flex-1 text-base font-medium"
                aria-label="Nome do funil"
              />
            </div>
            <button
              type="button"
              class="acl-funnel-assistant__remove text-xs text-n-ruby-11"
              @click="removeFunnel(funnelIndex)"
            >
              Excluir funil
            </button>
          </div>
          <div class="acl-funnel-assistant__fields">
            <label class="acl-funnel-assistant__field">
              <span>Definição do funil</span>
              <textarea
                v-model="funnel.description"
                rows="2"
                aria-label="Descrição do funil"
                placeholder="O que é este funil?"
              />
            </label>
            <label class="acl-funnel-assistant__field">
              <span>Critério de entrada</span>
              <textarea
                v-model="funnel.entry_criteria"
                rows="2"
                aria-label="Critério de entrada do funil"
                placeholder="Quando uma conversa deve entrar neste funil?"
              />
            </label>
          </div>

          <div
            v-for="(stage, stageIndex) in funnel.stages"
            :key="stageIndex"
            class="acl-funnel-assistant__stage"
          >
            <div class="acl-funnel-assistant__stage-header">
              <input
                v-model="stage.name"
                class="min-w-0 flex-1 font-medium"
                aria-label="Nome da etapa"
              />
              <button
                type="button"
                class="acl-funnel-assistant__remove text-xs text-n-ruby-11"
                @click="removeStage(funnel, stageIndex)"
              >
                Excluir
              </button>
            </div>
            <textarea
              v-model="stage.description"
              rows="2"
              aria-label="Descrição da etapa"
              placeholder="Descrição da etapa"
            />
            <textarea
              v-model="stage.entry_criteria"
              rows="2"
              aria-label="Critério de entrada da etapa"
              placeholder="Critério de entrada da etapa"
            />
            <div class="grid grid-cols-2 gap-2">
              <select
                v-model="stage.required_evidence_level"
                aria-label="Nível de evidência"
              >
                <option :value="null">Não definido</option>
                <option value="intencao">Intenção</option>
                <option value="tentativa">Tentativa</option>
                <option value="acao_concluida">Ação concluída</option>
              </select>
              <select v-model="stage.who_validates" aria-label="Quem valida">
                <option :value="null">Não definido</option>
                <option value="vendedor">Vendedor</option>
                <option value="cliente">Cliente</option>
                <option value="ambos">Vendedor e cliente</option>
              </select>
            </div>
          </div>
        </section>
        <span v-if="!proposal.funnels.length" class="text-sm text-n-ruby-11">
          A proposta ficou sem funis.
        </span>
        <div class="acl-funnel-assistant__actions">
          <NextButton
            type="button"
            faded
            slate
            label="Voltar"
            @click="state = 'input'"
          />
          <NextButton
            type="submit"
            label="Aprovar e Criar"
            :is-loading="busy"
            :disabled="!proposal.funnels.length"
          />
        </div>
      </div>
      <span v-if="error" class="mt-4 block text-xs text-n-ruby-11">{{
        error
      }}</span>
    </form>
  </Modal>
</template>

<style scoped>
.acl-funnel-assistant {
  display: flex;
  flex-direction: column;
  width: min(720px, 92vw);
  max-height: min(760px, 88vh);
  overflow: hidden;
  padding: 1.5rem;
}

.acl-funnel-assistant__header {
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--s-200, #e2e8f0);
}

.acl-funnel-assistant__heading,
.acl-funnel-assistant__funnel-title,
.acl-funnel-assistant__stage-header,
.acl-funnel-assistant__actions {
  display: flex;
  align-items: center;
}

.acl-funnel-assistant__heading {
  gap: 0.75rem;
}

.acl-funnel-assistant__badge,
.acl-funnel-assistant__step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 0.625rem;
  font-size: 0.7rem;
  font-weight: 700;
}

.acl-funnel-assistant__badge {
  width: 2rem;
  height: 2rem;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #2563eb);
}

.acl-funnel-assistant__content,
.acl-funnel-assistant__review {
  min-height: 0;
  padding-top: 1.25rem;
}

.acl-funnel-assistant__content,
.acl-funnel-assistant__review,
.acl-funnel-assistant__fields,
.acl-funnel-assistant__field {
  display: flex;
  flex-direction: column;
}

.acl-funnel-assistant__content,
.acl-funnel-assistant__review {
  gap: 1rem;
}

.acl-funnel-assistant__field {
  gap: 0.4rem;
}

.acl-funnel-assistant__field > span:first-child {
  font-weight: 600;
  color: var(--s-900, #0f172a);
}

.acl-funnel-assistant textarea,
.acl-funnel-assistant input,
.acl-funnel-assistant select {
  border: 1px solid var(--s-300, #cbd5e1);
  border-radius: 0.5rem;
  background: var(--s-25, #fff);
}

.acl-funnel-assistant textarea {
  min-height: 4rem;
  resize: vertical;
}

.acl-funnel-assistant textarea:focus,
.acl-funnel-assistant input:focus,
.acl-funnel-assistant select:focus {
  border-color: var(--s-500, #64748b);
  box-shadow: 0 0 0 2px rgb(100 116 139 / 15%);
  outline: none;
}

.acl-funnel-assistant__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
  padding: 3rem 1rem;
  text-align: center;
}

.acl-funnel-assistant__loader {
  width: 2rem;
  height: 2rem;
  border: 3px solid var(--s-200, #e2e8f0);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: acl-funnel-assistant-spin 0.8s linear infinite;
}

.acl-funnel-assistant__review {
  overflow-y: auto;
  padding-right: 0.25rem;
}

.acl-funnel-assistant__notice {
  display: flex;
  gap: 0.75rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid rgb(99 102 241 / 25%);
  border-radius: 0.625rem;
  background: rgb(99 102 241 / 7%);
}

.acl-funnel-assistant__notice-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex: 0 0 auto;
  border-radius: 50%;
  color: #fff;
  background: #6366f1;
  font-size: 0.7rem;
  font-weight: 700;
}

.acl-funnel-assistant__funnel {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 1rem;
  border: 1px solid var(--s-300, #cbd5e1);
  border-radius: 0.75rem;
  background: var(--s-25, #fff);
  box-shadow: 0 1px 2px rgb(15 23 42 / 4%);
}

.acl-funnel-assistant__funnel-header,
.acl-funnel-assistant__stage-header {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.acl-funnel-assistant__funnel-title {
  min-width: 0;
  gap: 0.6rem;
}

.acl-funnel-assistant__step-number {
  width: 1.5rem;
  height: 1.5rem;
  color: #4338ca;
  background: #e0e7ff;
}

.acl-funnel-assistant__fields {
  gap: 0.75rem;
}

.acl-funnel-assistant__stage {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0.8rem;
  border: 1px solid var(--s-200, #e2e8f0);
  border-radius: 0.625rem;
  background: var(--s-50, #f8fafc);
}

.acl-funnel-assistant__remove {
  flex: 0 0 auto;
  white-space: nowrap;
}

.acl-funnel-assistant__actions {
  position: sticky;
  bottom: -1.25rem;
  justify-content: flex-end;
  gap: 0.5rem;
  margin: 0 -0.25rem -0.25rem;
  padding: 0.85rem 0.25rem 0.25rem;
  background: linear-gradient(transparent, var(--s-25, #fff) 35%);
}

@keyframes acl-funnel-assistant-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .acl-funnel-assistant {
    width: 94vw;
    padding: 1rem;
  }

  .acl-funnel-assistant__actions {
    flex-wrap: wrap;
  }
}
</style>
